import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  InputAdornment,
  Collapse,
  Link,
  Tooltip,
} from '@mui/material';
import {
  Close,
  CheckCircle,
  OpenInNew,
  Info,
  Visibility,
  VisibilityOff,
  ContentCopy,
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import composioService from '../../services/composioService';
import { describeApiError } from '../../utils/apiError';
import { OAUTH_POPUP_NAME, openOAuthPopup, subscribeOAuthResult } from '../../utils/composioOAuthPopup';
import styles from './ComposioConnectionDialog.module.scss';

// Display-only: the user pastes it into their own OAuth app's redirect list.
// The backend would pass it to the connection as an unknown field, so it is
// never an input and never sent.
const REDIRECT_URI_FIELD = 'oauth_redirect_uri';

const STATUS_POLL_INTERVAL_MS = 2000;
const STATUS_POLL_TIMEOUT_MS = 2 * 60 * 1000;
const POPUP_CLOSED_CHECK_MS = 500;

// "Use ___ instead" links shown next to the one-click Connect button.
const ALT_SCHEME_LABELS = {
  API_KEY: 'an API key',
  BEARER_TOKEN: 'a bearer token',
  BASIC_AUTH: 'a username and password',
  OAUTH2: 'OAuth',
};

// Fallback secret detection for fields that don't carry an explicit is_secret flag.
const looksLikeSecret = (name = '') => {
  const n = String(name).toLowerCase();
  return /password|secret|token|apikey/.test(n) || /_key$/.test(n) || n === 'key';
};

// is_secret can be false on fields such as client_secret, so a secret-looking
// name masks the field either way.
const isSecretField = (field) => {
  if (!field) return false;
  return field.is_secret === true || looksLikeSecret(field.name);
};

// Auth modes that need redirect_url + an OAuth round-trip.
const isOAuthMode = (mode) => mode === 'OAUTH2' || mode === 'DCR_OAUTH';

// Flatten the new { auth_config: [], connection: [] } shape into a single
// array for rendering. The split is for the backend; the user sees one form.
const getSchemeFields = (scheme) => {
  if (!scheme) return [];
  const raw = scheme.fields;
  if (!raw) return [];
  if (Array.isArray(raw)) return raw; // tolerate legacy flat-array responses
  return [
    ...(Array.isArray(raw.auth_config) ? raw.auth_config : []),
    ...(Array.isArray(raw.connection) ? raw.connection : []),
  ];
};

const getRequiredFields = (scheme) =>
  getSchemeFields(scheme).filter((f) => f.required && f.name !== REDIRECT_URI_FIELD);

// One-click connect: an OAuth scheme Composio doesn't manage that needs nothing
// from the user — every DCR_OAUTH toolkit today. Decided from the fields, never
// from the toolkit slug, so new DCR toolkits are covered automatically.
const isOneClickScheme = (scheme) =>
  !!scheme && isOAuthMode(scheme.auth_mode) && !scheme.managed && getRequiredFields(scheme).length === 0;

// Choose which scheme to show first when the toolkit offers several.
const pickDefaultScheme = (schemes) => {
  if (!schemes || schemes.length === 0) return null;
  // DCR registers its own OAuth app, so it wins whenever the toolkit offers it.
  const dcr = schemes.find((s) => s.auth_mode === 'DCR_OAUTH');
  if (dcr) return dcr;
  if (schemes.length === 1) return schemes[0];
  const managedOAuth = schemes.find((s) => isOAuthMode(s.auth_mode) && s.managed);
  if (managedOAuth) return managedOAuth;
  const order = ['OAUTH2', 'API_KEY', 'BEARER_TOKEN', 'BASIC_AUTH', 'DCR_OAUTH', 'NO_AUTH'];
  for (const mode of order) {
    const scheme = schemes.find((s) => s.auth_mode === mode);
    if (scheme) return scheme;
  }
  return schemes[0];
};

// One-click schemes prefill defaults (e.g. scopes) in the "own OAuth app" section;
// credential forms start blank as before.
const initialFormValues = (scheme) => {
  const prefillDefaults = isOneClickScheme(scheme);
  const values = {};
  getSchemeFields(scheme).forEach((f) => {
    if (f.name === REDIRECT_URI_FIELD) return;
    values[f.name] = prefillDefaults && f.default != null ? String(f.default) : '';
  });
  return values;
};

/**
 * auth_config for "Use your own OAuth app", or null to connect one-click.
 * Only a typed credential (a field with no default, e.g. client_id) means the
 * user brought their own app — a prefilled default such as scopes alone does
 * not. Blank values are left out entirely: the backend only drops null, so
 * {"client_id": ""} would create an OAuth app with an empty client id.
 */
const buildOwnAppConfig = (scheme, values) => {
  const fields = getSchemeFields(scheme).filter((f) => f.name !== REDIRECT_URI_FIELD);
  const valueOf = (f) => String(values[f.name] ?? '').trim();
  if (!fields.some((f) => f.default == null && valueOf(f))) return null;
  const config = {};
  fields.forEach((f) => {
    const value = valueOf(f);
    if (value) config[f.name] = value;
  });
  return config;
};

const closePopup = (popup) => {
  try {
    if (popup && !popup.closed) popup.close();
  } catch {
    /* ignore */
  }
};

/**
 * Dialog for connecting to Composio apps.
 * Drives the form from GET /composio/apps/{toolkit_slug}/auth-fields so each
 * toolkit collects exactly the fields its selected auth scheme requires. OAuth
 * schemes with no required field (DCR_OAUTH) get a single Connect button that
 * signs in through a popup.
 */
const ComposioConnectionDialog = ({
  open,
  onClose,
  app,
  onConnect,
  onDisconnect,
  isConnected,
  connectionId,
  onRefresh,
  onConnected,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [oauthMessage, setOauthMessage] = useState(null);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [oauthUrl, setOauthUrl] = useState(null);
  const [showOAuthPrompt, setShowOAuthPrompt] = useState(false);

  // Auth metadata driven by /auth-fields.
  const [authFieldsLoading, setAuthFieldsLoading] = useState(false);
  const [authFieldsError, setAuthFieldsError] = useState(null);
  const [authSchemes, setAuthSchemes] = useState([]);
  const [noAuth, setNoAuth] = useState(false);
  const [defaultAuthMode, setDefaultAuthMode] = useState('');
  const [selectedAuthMode, setSelectedAuthMode] = useState('');
  const [formValues, setFormValues] = useState({});
  const [revealedSecrets, setRevealedSecrets] = useState({});

  // One-click connect: idle → starting → waiting | blocked → connected | failed.
  const [connectPhase, setConnectPhase] = useState('idle');
  const [connectUrl, setConnectUrl] = useState(null);
  const [connectError, setConnectError] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [copiedRedirect, setCopiedRedirect] = useState(false);
  const popupRef = useRef(null);
  const watchRef = useRef(null);
  // Bumped whenever a connect attempt ends, so late responses from it are ignored.
  const attemptRef = useRef(0);

  const selectedScheme = authSchemes.find((s) => s.auth_mode === selectedAuthMode) || null;
  const defaultScheme = authSchemes.find((s) => s.auth_mode === defaultAuthMode) || null;
  const isManagedOAuth = !!(selectedScheme && selectedScheme.managed && isOAuthMode(selectedScheme.auth_mode));
  const isOneClick = isOneClickScheme(selectedScheme);
  const fieldsToRender = !isManagedOAuth && selectedScheme ? getSchemeFields(selectedScheme) : [];
  const appId = app ? app.appId || app.id || app.key : null;

  // Toolkits whose default is one-click (e.g. vidiq_mcp: DCR_OAUTH + API_KEY)
  // offer the other schemes as "Use ___ instead" links instead of a dropdown.
  const hasOneClickDefault = isOneClickScheme(defaultScheme);
  const alternateSchemes = hasOneClickDefault
    ? authSchemes.filter((s) => s.auth_mode !== defaultAuthMode)
    : [];

  const toggleSecretVisibility = (name) => {
    setRevealedSecrets((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const resetTransientState = useCallback(() => {
    setError(null);
    setSuccess(false);
    setLoading(false);
    setOauthMessage(null);
    setConnectionStatus(null);
    setOauthUrl(null);
    setShowOAuthPrompt(false);
  }, []);

  const stopWatching = useCallback(() => {
    const watch = watchRef.current;
    if (!watch) return;
    clearInterval(watch.pollTimer);
    clearInterval(watch.closedTimer);
    watch.unsubscribe();
    watchRef.current = null;
  }, []);

  // Leaves an open login popup alone: the user may still finish signing in.
  const resetConnectFlow = useCallback(() => {
    attemptRef.current += 1;
    stopWatching();
    popupRef.current = null;
    setConnectPhase('idle');
    setConnectUrl(null);
    setConnectError(null);
    setAdvancedOpen(false);
    setCopiedRedirect(false);
  }, [stopWatching]);

  useEffect(
    () => () => {
      attemptRef.current += 1;
      stopWatching();
    },
    [stopWatching]
  );

  const loadAuthFields = useCallback(async (toolkitSlug) => {
    if (!toolkitSlug) return;
    setAuthFieldsLoading(true);
    setAuthFieldsError(null);
    setAuthSchemes([]);
    setNoAuth(false);
    setDefaultAuthMode('');
    setSelectedAuthMode('');
    setFormValues({});
    try {
      const data = await composioService.getAuthFields(toolkitSlug);
      const schemes = Array.isArray(data?.auth_schemes) ? data.auth_schemes : [];
      setAuthSchemes(schemes);
      setNoAuth(Boolean(data?.no_auth));

      if (data?.no_auth) {
        setSelectedAuthMode('NO_AUTH');
        setFormValues({});
      } else {
        const picked = pickDefaultScheme(schemes);
        if (picked) {
          setDefaultAuthMode(picked.auth_mode);
          setSelectedAuthMode(picked.auth_mode);
          setFormValues(initialFormValues(picked));
        }
      }
    } catch (err) {
      setAuthFieldsError(describeApiError(err, 'Failed to load authentication options for this app.'));
    } finally {
      setAuthFieldsLoading(false);
    }
  }, []);

  // Open / close + app-change reset, plus OAuth-callback handling.
  useEffect(() => {
    if (!open) {
      resetTransientState();
      resetConnectFlow();
      setAuthSchemes([]);
      setNoAuth(false);
      setDefaultAuthMode('');
      setSelectedAuthMode('');
      setFormValues({});
      setAuthFieldsError(null);
      return;
    }

    resetTransientState();

    // OAuth callback detection (preserved from previous behaviour).
    const urlParams = new URLSearchParams(window.location.search);
    const oauthSuccess = urlParams.get('oauth_success');
    const oauthError = urlParams.get('oauth_error');

    if (oauthSuccess === 'true') {
      setSuccess(true);
      setOauthMessage('Successfully connected! You can now use this app in your AI agents.');
      setConnectionStatus('connected');
      window.history.replaceState({}, document.title, window.location.pathname);
      const timer = setTimeout(() => {
        onClose();
        if (onRefresh) onRefresh();
      }, 3000);
      return () => clearTimeout(timer);
    }

    if (oauthError === 'true') {
      setError('OAuth authentication failed. Please try again.');
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // Only fetch auth metadata when we're showing the connect flow.
    if (app && !isConnected) {
      resetConnectFlow();
      loadAuthFields(app.key);
    }
  }, [open, isConnected, app?.key, loadAuthFields, onClose, onRefresh, resetTransientState, resetConnectFlow, app]);

  const selectAuthMode = (mode) => {
    resetConnectFlow();
    setSelectedAuthMode(mode);
    setFormValues(initialFormValues(authSchemes.find((s) => s.auth_mode === mode)));
    setRevealedSecrets({});
  };

  const handleAuthModeChange = (event) => {
    selectAuthMode(event.target.value);
  };

  const handleFieldChange = (name, value) => {
    setFormValues((prev) => ({ ...prev, [name]: value }));
  };

  const isConnectDisabled = () => {
    if (loading || authFieldsLoading) return true;
    if (authFieldsError) return true;
    if (!selectedAuthMode) return true;
    if (selectedAuthMode === 'NO_AUTH') return false;
    if (isManagedOAuth) return false;
    return getRequiredFields(selectedScheme).some((f) => !String(formValues[f.name] || '').trim());
  };

  const handleConnect = async () => {
    setLoading(true);
    setError(null);

    try {
      const connectData = {
        authMode: selectedAuthMode,
        toolkit_slug: app.key,
        toolkit_name: app.name,
      };

      // Managed OAuth and NO_AUTH connections must not send auth_config.
      if (!isManagedOAuth && selectedAuthMode !== 'NO_AUTH') {
        const trimmed = {};
        Object.entries(formValues).forEach(([k, v]) => {
          const value = typeof v === 'string' ? v.trim() : v;
          if (value !== '' && value !== null && value !== undefined) {
            trimmed[k] = value;
          }
        });
        if (Object.keys(trimmed).length > 0) {
          connectData.authConfig = trimmed;
        }
      }

      const result = await onConnect(appId, connectData);
      const { connection_url, status, message } = result || {};
      const normalizedStatus = String(status || '').toLowerCase();
      // `active` means the connection is fully established — no OAuth round-trip needed,
      // regardless of auth mode. The backend sometimes still returns a connection_url
      // (e.g. the local /oauth/callback URL) which we must ignore in this case.
      const isActive = normalizedStatus === 'active' || normalizedStatus === 'connected';
      const isPending =
        normalizedStatus === 'pending' ||
        normalizedStatus === 'initiated' ||
        normalizedStatus === 'initializing' ||
        normalizedStatus === 'initialising';

      if (isActive) {
        setSuccess(true);
        setOauthMessage(message || `Successfully connected to ${app.name}`);
        setConnectionStatus('connected');
        setTimeout(() => {
          onClose();
          if (onRefresh) onRefresh();
        }, 2000);
      } else if (connection_url && (isPending || isOAuthMode(selectedAuthMode))) {
        if (message) {
          setOauthMessage(message);
          setConnectionStatus(status || 'pending');
        }
        setOauthUrl(connection_url);
        setShowOAuthPrompt(true);
        setError(null);
        setLoading(false);
      } else {
        setSuccess(true);
        setOauthMessage(message || `Successfully connected to ${app.name}`);
        setConnectionStatus('connected');
        setTimeout(() => {
          onClose();
          if (onRefresh) onRefresh();
        }, 2000);
      }
    } catch (err) {
      setError(describeApiError(err, 'Failed to connect. Please try again.'));
      setLoading(false);
    }
  };

  const handleProceedToOAuth = () => {
    if (oauthUrl) {
      window.location.href = oauthUrl;
    }
  };

  // ---- One-click connect -------------------------------------------------

  const finishConnected = useCallback(() => {
    attemptRef.current += 1;
    stopWatching();
    closePopup(popupRef.current);
    popupRef.current = null;
    setConnectError(null);
    setConnectPhase('connected');
    // Re-read connections in place so the dialog flips to Connected + Disconnect.
    if (onConnected) onConnected();
    else if (onRefresh) onRefresh();
  }, [stopWatching, onConnected, onRefresh]);

  const failConnect = useCallback(
    (message) => {
      attemptRef.current += 1;
      stopWatching();
      setConnectError(message);
      setConnectPhase('failed');
    },
    [stopWatching]
  );

  // GET /composio/status/{app_id}; `connected: true` means done.
  const checkStatus = useCallback(
    async (attempt) => {
      try {
        const result = await composioService.getConnectionStatus(appId);
        if (attempt !== attemptRef.current) return null;
        if (result?.connected || String(result?.status || '').toLowerCase() === 'active') {
          finishConnected();
          return { ...result, connected: true };
        }
        return result;
      } catch {
        return null;
      }
    },
    [appId, finishConnected]
  );

  // Three ways to learn the login finished: the callback page's broadcast, the
  // popup closing, and a 2 s status poll for up to 2 minutes (covers a lost
  // opener link or a login finished in another tab). Only the poll times out;
  // the other two keep listening while the dialog is open.
  const startWatching = useCallback(
    (attempt) => {
      stopWatching();
      const deadline = Date.now() + STATUS_POLL_TIMEOUT_MS;
      let callbackSucceeded = false;

      const pollTimer = setInterval(() => {
        if (Date.now() > deadline) {
          clearInterval(pollTimer);
          // Still in the popup: its close or the callback broadcast will tell us.
          if (!popupRef.current) {
            setConnectPhase((phase) => (phase === 'waiting' || phase === 'blocked' ? 'idle' : phase));
          }
          return;
        }
        checkStatus(attempt);
      }, STATUS_POLL_INTERVAL_MS);

      const closedTimer = setInterval(async () => {
        const popup = popupRef.current;
        if (!popup || !popup.closed) return;
        popupRef.current = null;
        // The callback page closes itself after a success; let the poll confirm it.
        if (callbackSucceeded) return;
        const result = await checkStatus(attempt);
        if (attempt !== attemptRef.current || result?.connected) return;
        if (/fail|error|expired/i.test(String(result?.status || '')) && result?.error_message) {
          failConnect(result.error_message);
          return;
        }
        // Closed without finishing (the connection stays pending), or the
        // provider's page cut our link to the window. Offer Connect again, but
        // keep polling quietly until the deadline in case the login goes on.
        setConnectPhase((phase) => (phase === 'waiting' ? 'idle' : phase));
      }, POPUP_CLOSED_CHECK_MS);

      const unsubscribe = subscribeOAuthResult((result) => {
        if (attempt !== attemptRef.current) return;
        if (result.status === 'success') {
          callbackSucceeded = true;
          checkStatus(attempt);
        } else if (result.status === 'error') {
          // The callback can fail after Composio already activated the
          // connection, so confirm with the status endpoint before failing.
          checkStatus(attempt).then((status) => {
            if (attempt !== attemptRef.current || status?.connected) return;
            failConnect(result.message || `The connection to ${app?.name || 'the app'} failed.`);
          });
        }
      });

      watchRef.current = { pollTimer, closedTimer, unsubscribe };
    },
    [stopWatching, checkStatus, failConnect, app?.name]
  );

  const handleOneClickConnect = async () => {
    // Open the popup before any await so the browser treats it as user-initiated.
    const popup = openOAuthPopup();
    attemptRef.current += 1;
    const attempt = attemptRef.current;
    stopWatching();
    popupRef.current = popup;
    setConnectError(null);
    setConnectUrl(null);
    setConnectPhase('starting');

    const connectData = {
      authMode: selectedAuthMode,
      toolkit_slug: app.key,
      toolkit_name: app.name,
    };
    const ownApp = advancedOpen ? buildOwnAppConfig(selectedScheme, formValues) : null;
    if (ownApp) connectData.authConfig = ownApp;

    try {
      const result = await onConnect(appId, connectData);
      if (attempt !== attemptRef.current) {
        closePopup(popup);
        return;
      }

      const status = String(result?.status || '').toLowerCase();
      if (status === 'active' || status === 'connected') {
        closePopup(popup);
        finishConnected();
        return;
      }
      if (!result?.connection_url) {
        throw new Error(result?.message || `${app.name} didn't return a login page. Please try again.`);
      }

      setConnectUrl(result.connection_url);
      if (!popup) {
        setConnectPhase('blocked');
      } else if (popup.closed) {
        // Closed the placeholder window before the login page loaded.
        setConnectPhase('idle');
        return;
      } else {
        popup.location.href = result.connection_url;
        setConnectPhase('waiting');
      }
      startWatching(attempt);
    } catch (err) {
      if (attempt !== attemptRef.current) return;
      closePopup(popup);
      setConnectError(describeApiError(err, `Couldn't start the connection to ${app.name}. Please try again.`));
      setConnectPhase('failed');
    }
  };

  const handleReopenLogin = () => {
    if (!connectUrl) return;
    const popup = openOAuthPopup(connectUrl);
    if (popup) {
      popupRef.current = popup;
      setConnectPhase('waiting');
    } else {
      setConnectPhase('blocked');
    }
  };

  const handleCopyRedirect = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      setCopiedRedirect(true);
      setTimeout(() => setCopiedRedirect(false), 1500);
    } catch {
      /* clipboard unavailable — the value is still selectable */
    }
  };

  // ------------------------------------------------------------------------

  const handleDisconnect = async () => {
    setLoading(true);
    setError(null);
    try {
      await onDisconnect(appId);
      setSuccess(true);
      setTimeout(() => {
        onClose();
        if (onRefresh) onRefresh();
      }, 1500);
    } catch (err) {
      setError(describeApiError(err, 'Failed to disconnect. Please try again.'));
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  if (!app) return null;

  const availableModes = authSchemes.map((s) => s.auth_mode);
  const authReady = !authFieldsLoading && !authFieldsError;

  const renderAuthField = (field) => {
    const secret = isSecretField(field);
    const revealed = !!revealedSecrets[field.name];
    const label = field.display_name || field.name;
    return (
      <TextField
        key={field.name}
        fullWidth
        size="small"
        label={label}
        type={secret && !revealed ? 'password' : 'text'}
        value={formValues[field.name] || ''}
        onChange={(e) => handleFieldChange(field.name, e.target.value)}
        required={!!field.required}
        helperText={field.description}
        autoComplete={secret ? 'new-password' : 'off'}
        InputProps={
          secret
            ? {
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label={revealed ? 'Hide value' : 'Show value'}
                      onClick={() => toggleSecretVisibility(field.name)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                    >
                      {revealed ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }
            : undefined
        }
        sx={{ mb: 2 }}
      />
    );
  };

  // Read-only redirect URI with a Copy button, for the user's own OAuth app.
  const renderRedirectUri = (field) => {
    const value = field?.default;
    if (!value) return null;
    return (
      <Box key={field.name} sx={{ mb: 2 }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {field.display_name || 'Redirect URI'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pl: 1.5,
            pr: 0.5,
            py: 0.5,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', fontSize: '0.8rem', flex: 1, minWidth: 0, wordBreak: 'break-all' }}
          >
            {value}
          </Typography>
          <Tooltip title={copiedRedirect ? 'Copied' : 'Copy'}>
            <IconButton size="small" onClick={() => handleCopyRedirect(value)} aria-label="Copy redirect URI">
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Add this to your OAuth app's allowed redirect URLs.
        </Typography>
      </Box>
    );
  };

  const renderAdvanced = () => {
    const inputFields = fieldsToRender.filter((f) => f.name !== REDIRECT_URI_FIELD);
    const redirectField = fieldsToRender.find((f) => f.name === REDIRECT_URI_FIELD);
    if (inputFields.length === 0 && !redirectField?.default) return null;
    return (
      <Box sx={{ mt: 2 }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          underline="hover"
          onClick={() => setAdvancedOpen((prev) => !prev)}
          aria-expanded={advancedOpen}
          sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
        >
          {advancedOpen ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          Use your own OAuth app (optional)
        </Link>
        <Collapse in={advancedOpen}>
          <Box sx={{ pt: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              For your own branding on the consent screen or your own rate limits. Close this section to
              use the standard sign-in.
            </Typography>
            {inputFields.map(renderAuthField)}
            {renderRedirectUri(redirectField)}
          </Box>
        </Collapse>
      </Box>
    );
  };

  const renderOneClick = () => {
    const awaitingLogin = connectPhase === 'waiting' || connectPhase === 'blocked';
    const showOptions = !awaitingLogin && connectPhase !== 'connected';
    return (
      <Box sx={{ mb: 1 }}>
        {connectPhase === 'failed' && connectError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {connectError}
          </Alert>
        )}

        {connectPhase === 'waiting' && (
          <Box
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, borderRadius: 2, bgcolor: 'action.hover' }}
          >
            <CircularProgress size={20} sx={{ mt: 0.25, flexShrink: 0 }} />
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Finish signing in to {app.name} in the window that opened.
              </Typography>
              <Link
                component="button"
                type="button"
                variant="body2"
                underline="hover"
                onClick={handleReopenLogin}
                sx={{ mt: 0.5 }}
              >
                Reopen login page
              </Link>
            </Box>
          </Box>
        )}

        {connectPhase === 'blocked' && (
          <Alert severity="warning">
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
              Your browser blocked the login window.
            </Typography>
            <Link href={connectUrl} target={OAUTH_POPUP_NAME} variant="body2">
              Open the {app.name} login page
            </Link>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              This dialog updates on its own once you finish signing in.
            </Typography>
          </Alert>
        )}

        {connectPhase === 'connected' && (
          <Alert severity="success">Connected to {app.name}. You can now use it in your agents.</Alert>
        )}

        {showOptions && (
          <>
            <Typography variant="body2" color="text.secondary">
              No setup needed. You'll sign in on {app.name}'s own page. Fouray never sees your password.
            </Typography>

            {alternateSchemes.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 2, rowGap: 0.5, mt: 1.5 }}>
                {alternateSchemes.map((scheme) => (
                  <Link
                    key={scheme.auth_mode}
                    component="button"
                    type="button"
                    variant="body2"
                    underline="hover"
                    onClick={() => selectAuthMode(scheme.auth_mode)}
                  >
                    Use {ALT_SCHEME_LABELS[scheme.auth_mode] || scheme.auth_mode} instead
                  </Link>
                ))}
              </Box>
            )}

            {renderAdvanced()}
          </>
        )}
      </Box>
    );
  };

  const renderOneClickActions = () => {
    if (connectPhase === 'waiting' || connectPhase === 'blocked' || connectPhase === 'connected') return null;
    const starting = connectPhase === 'starting';
    return (
      <Button
        onClick={handleOneClickConnect}
        variant="contained"
        disabled={starting}
        startIcon={starting ? <CircularProgress size={16} /> : <OpenInNew />}
        className={styles.saveButton}
      >
        {starting ? 'Opening login…' : connectPhase === 'failed' ? 'Try again' : `Connect ${app.name}`}
      </Button>
    );
  };

  const showOneClick = !isConnected && authReady && !noAuth && isOneClick;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      className={styles.dialog}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgba(24, 24, 27, 0.4)',
            backdropFilter: 'blur(2px)',
          },
        },
      }}
      PaperProps={{
        className: styles.dialogPaper,
      }}
    >
      <DialogTitle className={styles.dialogTitle} component="div">
        <Box className={styles.titleRow} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {app.logo && (
            <Box
              component="img"
              src={app.logo}
              alt={app.name}
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1,
                objectFit: 'contain',
                flexShrink: 0,
              }}
            />
          )}
          <Box sx={{ minWidth: 0 }}>
            <Typography component="h3" variant="h6" className={styles.title} sx={{ fontWeight: 600 }}>
              {app.name}
            </Typography>
            {isConnected && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                <Typography variant="caption" color="success.main">
                  Connected
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
        <IconButton onClick={handleClose} disabled={loading} className={styles.closeButton} aria-label="Close">
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent className={styles.dialogContent}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {isConnected ? 'Successfully disconnected!' : 'Successfully connected!'}
          </Alert>
        )}

        {oauthMessage && (
          <Alert
            severity={connectionStatus === 'pending' ? 'info' : connectionStatus === 'connected' ? 'success' : 'info'}
            sx={{ mb: 2 }}
            icon={<OpenInNew />}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {oauthMessage}
            </Typography>
            {connectionStatus === 'pending' && showOAuthPrompt && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
                Click the button below to complete the OAuth authentication. After completing the flow, you'll be redirected back to this page.
              </Typography>
            )}
            {connectionStatus === 'connected' && (
              <Typography variant="caption" color="text.secondary">
                The app is now connected and ready to use in your AI agents.
              </Typography>
            )}
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {app.description}
        </Typography>

        {showOneClick && renderOneClick()}

        {!isConnected && !showOneClick && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                Authentication Method
              </Typography>

              {authFieldsLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={16} />
                  <Typography variant="body2" color="text.secondary">
                    Loading authentication options…
                  </Typography>
                </Box>
              )}

              {authFieldsError && !authFieldsLoading && (
                <Alert
                  severity="error"
                  sx={{ mb: 2 }}
                  action={
                    <Button color="inherit" size="small" onClick={() => loadAuthFields(app.key)}>
                      Retry
                    </Button>
                  }
                >
                  {authFieldsError}
                </Alert>
              )}

              {authReady && noAuth && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This app does not require authentication. Click Connect to enable it for your agents.
                </Alert>
              )}

              {authReady && !noAuth && authSchemes.length > 0 && (
                <>
                  {hasOneClickDefault && (
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      underline="hover"
                      onClick={() => selectAuthMode(defaultAuthMode)}
                      sx={{ display: 'block', mb: 2 }}
                    >
                      Sign in with {app.name} instead
                    </Link>
                  )}

                  {authSchemes.length > 1 && !hasOneClickDefault && (
                    <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                      <InputLabel>Authentication Mode</InputLabel>
                      <Select
                        value={selectedAuthMode}
                        onChange={handleAuthModeChange}
                        label="Authentication Mode"
                      >
                        {authSchemes.map((scheme) => (
                          <MenuItem key={scheme.auth_mode} value={scheme.auth_mode}>
                            {scheme.auth_mode}
                            {scheme.managed ? ' (managed by Composio)' : ''}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {isManagedOAuth && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Composio manages the OAuth credentials for this app. Click Connect to start the
                      authorisation flow — you'll be redirected to sign in and authorise access.
                    </Alert>
                  )}

                  {!isManagedOAuth && isOAuthMode(selectedAuthMode) && fieldsToRender.length > 0 && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      You're providing credentials for your own OAuth app registered with this service
                      (e.g. via the Shopify Partners dashboard or GitHub OAuth Apps). Paste the values
                      from that app below.
                    </Alert>
                  )}

                  {fieldsToRender.map((field) =>
                    field.name === REDIRECT_URI_FIELD ? renderRedirectUri(field) : renderAuthField(field)
                  )}

                  {availableModes.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                        Available:
                      </Typography>
                      {availableModes.map((mode) => (
                        <Chip
                          key={mode}
                          label={mode}
                          size="small"
                          color={mode === selectedAuthMode ? 'primary' : 'default'}
                          variant={mode === selectedAuthMode ? 'filled' : 'outlined'}
                          sx={{ fontSize: '0.7rem', height: 24 }}
                        />
                      ))}
                    </Box>
                  )}
                </>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
              <Info sx={{ fontSize: 20, color: 'info.main', mt: 0.2 }} />
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                  What happens when you connect?
                </Typography>
                <List dense sx={{ pl: 0 }}>
                  {isOAuthMode(selectedAuthMode) ? (
                    <>
                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="You'll be redirected to the app's OAuth page"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Complete authentication and authorize access"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="You'll be redirected back to this page"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    </>
                  ) : selectedAuthMode === 'NO_AUTH' || noAuth ? (
                    <ListItem sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 28 }}>
                        <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText
                        primary="No credentials needed — the app is enabled immediately"
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ) : (
                    <>
                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Enter the credentials shown above"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon sx={{ minWidth: 28 }}>
                          <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                        </ListItemIcon>
                        <ListItemText
                          primary="Click Connect to authenticate immediately"
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    </>
                  )}
                  <ListItem sx={{ py: 0.5, px: 0 }}>
                    <ListItemIcon sx={{ minWidth: 28 }}>
                      <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                    </ListItemIcon>
                    <ListItemText
                      primary="Use the connected app in your AI agents"
                      primaryTypographyProps={{ variant: 'body2' }}
                    />
                  </ListItem>
                </List>
              </Box>
            </Box>
          </>
        )}

        {isConnected && (
          <Box sx={{ mt: 2 }}>
            {connectPhase === 'connected' && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Connected to {app.name}. You can now use it in your agents.
              </Alert>
            )}
            <Alert severity="warning" sx={{ mb: 2 }}>
              Disconnecting will remove access to this app from all your agents.
            </Alert>
            <Typography variant="body2" color="text.secondary">
              Connection ID: <code>{connectionId}</code>
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions className={styles.dialogActions}>
        <Button
          onClick={handleClose}
          disabled={loading}
          variant="text"
          className={styles.cancelButton}
        >
          {connectPhase === 'connected' ? 'Close' : 'Cancel'}
        </Button>

        {!isConnected ? (
          showOneClick ? (
            renderOneClickActions()
          ) : showOAuthPrompt ? (
            <Button
              onClick={handleProceedToOAuth}
              variant="contained"
              startIcon={<OpenInNew />}
              className={styles.saveButton}
            >
              Proceed to OAuth
            </Button>
          ) : (
            <Button
              onClick={handleConnect}
              variant="contained"
              disabled={isConnectDisabled()}
              startIcon={loading ? <CircularProgress size={16} /> : <OpenInNew />}
              className={styles.saveButton}
            >
              {loading ? 'Connecting...' : 'Connect App'}
            </Button>
          )
        ) : (
          <Button
            onClick={handleDisconnect}
            variant="outlined"
            color="error"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {loading ? 'Disconnecting...' : 'Disconnect'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ComposioConnectionDialog;
